// src/services/nativeNotificationService.js
import { Capacitor } from "@capacitor/core";
import { LocalNotifications } from "@capacitor/local-notifications";
import { PushNotifications } from "@capacitor/push-notifications";
import { collection, query, orderBy, limit, onSnapshot, doc, updateDoc, arrayUnion } from "firebase/firestore";
import { firestore } from "../firebase";

const PROCESSED_KEY = "maptrees_notified_ids";

// Carregar IDs de notificações já disparadas do localStorage
const getProcessedNotifIds = () => {
  try {
    const raw = localStorage.getItem(PROCESSED_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
};

const saveProcessedNotifId = (id) => {
  try {
    const set = getProcessedNotifIds();
    set.add(id);
    const arr = Array.from(set).slice(-100);
    localStorage.setItem(PROCESSED_KEY, JSON.stringify(arr));
  } catch {}
};

let isInitialized = false;

// Resolver texto da notificação para todos os tipos suportados no site
export const resolveNotificationText = (type, customMsg) => {
  if (customMsg && type === "newInfo") return customMsg;
  if (customMsg && type === "contactReply") return customMsg;

  switch (type) {
    case "newFollower":
      return "começou a seguir você";
    case "newIdentified":
      return "identificou uma das suas árvores";
    case "newPost":
      return "publicou uma nova foto";
    case "newLike":
      return "curtiu a sua publicação";
    case "newLikeBlog":
      return "curtiu o seu artigo";
    case "newComment":
      return "comentou na sua publicação";
    case "replyComment":
      return "respondeu ao seu comentário";
    case "newCommentBlog":
      return "comentou no seu artigo";
    case "newFavorite":
      return "favoritou sua árvore";
    case "newDuplicated":
      return "Árvore duplicada detectada";
    case "newLocation":
      return "Atualização de localização pendente";
    case "newArticle":
      return "publicou um novo artigo";
    case "newSize":
      return "alterou o porte da sua árvore";
    case "contactReply":
      return "Enviou uma mensagem";
    default:
      return customMsg || "Nova notificação no MapTrees";
  }
};

/**
 * Inicializa a escuta de notificações nativas para Android via Capacitor
 */
export const initNativeNotifications = async (userId, onNavigate) => {
  if (!Capacitor.isNativePlatform() || !userId) return;

  try {
    // 1. Solicitar permissões nativas de notificação
    try {
      const localPerm = await LocalNotifications.requestPermissions();
      if (localPerm.display !== "granted") {
        console.warn("Permissão de notificação local não concedida");
      }
    } catch (e) {
      console.warn("Erro ao solicitar permissão de LocalNotifications:", e);
    }

    try {
      const pushPerm = await PushNotifications.requestPermissions();
      if (pushPerm.receive === "granted") {
        await PushNotifications.register();
      }
    } catch (e) {
      console.warn("PushNotifications register:", e);
    }

    // 2. Registrador do Token FCM (para receber notificações com o app fechado)
    if (!isInitialized) {
      try {
        PushNotifications.addListener("registration", async (token) => {
          if (token.value && userId) {
            try {
              const userRef = doc(firestore, "users", userId);
              await updateDoc(userRef, {
                fcmTokens: arrayUnion(token.value),
                lastFcmToken: token.value
              });
            } catch (err) {
              console.warn("Erro ao salvar fcmToken no Firestore:", err);
            }
          }
        });
      } catch (e) {}

      // 3. Criar canal de notificação nativo no Android com som, vibração e ícone
      await LocalNotifications.createChannel({
        id: "maptrees_notifications",
        name: "Notificações MapTrees",
        description: "Alertas e mensagens do MapTrees",
        importance: 5, // Importância máxima (High)
        visibility: 1,  // Visibilidade pública no lockscreen
        sound: "default",
        vibration: true
      });

      // 4. Registrar handlers de clique na notificação nativa
      LocalNotifications.addListener(
        "localNotificationActionPerformed",
        (action) => {
          const targetLink = action.notification?.extra?.link;
          if (targetLink && typeof onNavigate === "function") {
            let path = targetLink.trim();
            if (!path.startsWith("http://") && !path.startsWith("https://") && !path.startsWith("/")) {
              path = "/" + path;
            }
            onNavigate(path);
          }
        }
      );

      try {
        PushNotifications.addListener(
          "pushNotificationActionPerformed",
          (action) => {
            const targetLink = action.notification?.data?.link;
            if (targetLink && typeof onNavigate === "function") {
              let path = targetLink.trim();
              if (!path.startsWith("http://") && !path.startsWith("https://") && !path.startsWith("/")) {
                path = "/" + path;
              }
              onNavigate(path);
            }
          }
        );
      } catch (e) {}

      isInitialized = true;
    }

    // 5. Escutar subcoleção de notificações do usuário com controle de carga inicial
    const notiRef = collection(firestore, "users", userId, "notifications");
    const q = query(notiRef, orderBy("createdDate", "desc"), limit(10));

    let isInitialSnapshot = true;

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const processedSet = getProcessedNotifIds();

      if (isInitialSnapshot) {
        // Na primeira carga ao iniciar o app, apenas registra os IDs já existentes
        // para que NÃO sejam renotificados novamente!
        snapshot.docs.forEach((docSnap) => {
          processedSet.add(docSnap.id);
          saveProcessedNotifId(docSnap.id);
        });
        isInitialSnapshot = false;
        return;
      }

      snapshot.docChanges().forEach(async (change) => {
        if (change.type === "added") {
          const notifData = change.doc.data();
          const notifId = change.doc.id;

          if (!notifData.read && !processedSet.has(notifId)) {
            saveProcessedNotifId(notifId);
            processedSet.add(notifId);

            const senderName = notifData.customDisplayName || notifData.displayName || "MapTrees";
            const bodyText = resolveNotificationText(notifData.ntType, notifData.message);
            const fullBody = `${senderName} ${bodyText}`.trim();

            const numericId = Math.abs(
              notifId.split("").reduce((acc, char) => (acc << 5) - acc + char.charCodeAt(0), 0)
            ) % 2147483647;

            await LocalNotifications.schedule({
              notifications: [
                {
                  id: numericId || Date.now(),
                  title: "MapTrees",
                  body: fullBody,
                  smallIcon: "ic_stat_maptrees",
                  largeIcon: "ic_launcher_round",
                  iconColor: "#10B981",
                  channelId: "maptrees_notifications",
                  extra: {
                    link: notifData.link || "/centralnotifications",
                    notifId: notifId
                  }
                }
              ]
            });
          }
        }
      });
    });

    return unsubscribe;
  } catch (err) {
    console.error("Erro ao inicializar notificações nativas:", err);
  }
};
